import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Hospital } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="rounded-lg bg-blue-600 p-2">
              <Hospital className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">AMCARE</h1>
          </Link>
          <Button variant="outline" asChild>
            <Link to="/login">Login</Link>
          </Button>
        </div>
      </header>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Privacy Policy</CardTitle>
            <p className="text-sm text-muted-foreground mt-2">
              Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            <section>
              <p className="text-gray-700 mb-4">
                AMCare ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our 
                healthcare management SaaS platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">1. Information We Collect</h2>
              <div className="space-y-3">
                <div>
                  <h3 className="font-semibold mb-2">Account Information</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Name, email address, and contact information</li>
                    <li>Hospital/clinic details and credentials</li>
                    <li>User role and permissions</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Patient Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Patient demographics and medical information</li>
                    <li>Prescriptions and treatment records</li>
                    <li>Billing and payment information</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Usage Data</h3>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>System logs and access patterns</li>
                    <li>Feature usage and preferences</li>
                    <li>Device and browser information</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Provide and maintain our healthcare management services</li>
                <li>Process payments and manage subscriptions</li>
                <li>Send important notifications and updates</li>
                <li>Improve our services and user experience</li>
                <li>Comply with legal obligations and regulatory requirements</li>
                <li>Protect against fraud and security threats</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">3. Data Security</h2>
              <p className="text-gray-700 mb-2">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Encryption of data in transit and at rest</li>
                <li>Secure authentication and access controls</li>
                <li>Regular security audits and updates</li>
                <li>Multi-tenant data isolation</li>
                <li>Compliance with healthcare data regulations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">4. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 mb-2">
                We do not sell your data. We may share information only in the following circumstances:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>With your explicit consent</li>
                <li>To comply with legal obligations or court orders</li>
                <li>With service providers who assist in operations (under strict confidentiality agreements)</li>
                <li>To protect our rights, property, or safety</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">5. Data Retention</h2>
              <p className="text-gray-700">
                We retain your data for as long as necessary to provide our services and comply with 
                legal obligations. Healthcare records are retained according to applicable healthcare 
                data retention requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">6. Your Rights</h2>
              <p className="text-gray-700 mb-2">You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                <li>Access your personal information</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data (subject to legal requirements)</li>
                <li>Object to processing of your data</li>
                <li>Data portability</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">7. Cookies and Tracking</h2>
              <p className="text-gray-700">
                We use cookies and similar technologies to enhance user experience, analyze usage, 
                and maintain security. You can control cookie preferences through your browser settings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">8. Children's Privacy</h2>
              <p className="text-gray-700">
                Our service is not intended for individuals under 18 years of age. We do not knowingly 
                collect personal information from children.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">9. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of any 
                significant changes by email or through the platform.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold mb-3">10. Contact Us</h2>
              <p className="text-gray-700 mb-2">
                For questions about this Privacy Policy or your data, please contact us:
              </p>
              <div className="text-gray-700">
                <p>Email: <a href="mailto:support@amcares.in" className="text-blue-600 hover:underline">support@amcares.in</a></p>
                <p className="mt-2">Visit: <a href="/contact-us" className="text-blue-600 hover:underline">Contact Us Page</a></p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="container mx-auto px-4 py-6 text-center text-sm text-gray-600">
          <p>&copy; {new Date().getFullYear()} AMCare. All rights reserved.</p>
          <div className="mt-2 flex justify-center gap-4">
            <Link to="/terms-and-conditions" className="hover:underline">Terms & Conditions</Link>
            <Link to="/contact-us" className="hover:underline">Contact Us</Link>
            <Link to="/refund-policy" className="hover:underline">Refund Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

